import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import {LockClosedIcon} from '@heroicons/react-v2/20/solid';

import Button, {ButtonTypes} from '../../components/ButtonNew';
import {cx} from '../../util';
import Link from '../../components/Link';
import {RightArrowIcon, LeftArrowIcon} from '../../components/svg';

const GuideCard = ({
  guide, onClick, isLocked = false, className = 'min-w-[260px] max-w-[280px] flex-shrink-0 snap-start',
}) => {
  const guideUrl = `/guides/${guide.slug}`;
  const subtitle = guide.name
    ? `${guide.companyName} ${guide.roleName}`.trim()
    : guide.companyName;

  return (
    <Link
      href={guideUrl}
      onClick={(e) => onClick(e, guide)}
      className={`block ${className}`}
    >
      <div className="flex flex-col h-full text-left p-5 rounded-xl border border-gray-300 hover:border-gray-400 hover:shadow-sm transition-all duration-150">
        <div className="flex items-start justify-between mb-3">
          {guide.companyLogo ? (
            <div className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={guide.companyLogo}
                alt={guide.companyName}
                className="w-7 h-7 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-gray-500">
                {guide.companyName?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          )}
          {isLocked && (
            <LockClosedIcon className="w-4 h-4 text-gray-400 shrink-0" />
          )}
          {!isLocked && guide.trending && (
            <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
              Trending
            </span>
          )}
        </div>

        <h3 className="text-base font-semibold text-gray-900 leading-snug mb-1 line-clamp-2">
          {subtitle}
        </h3>

        <div className="flex-1 mb-4">
          {guide.intro && (
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
              {guide.intro}
            </p>
          )}
        </div>

        <div className="mt-auto">
          <div className="w-full flex items-center justify-center mb-2 px-4 py-2 rounded border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
            View interview guide
            <RightArrowIcon className="w-3.5 ml-1.5" stroke="currentColor" />
          </div>
          {(guide.numExperiences > 0 || guide.updatedAt) && (
            <p className="text-xs text-gray-400 text-center">
              {guide.numExperiences > 0 && (
                <>
                  {guide.numExperiences}
                  {' '}
                  {guide.numExperiences === 1 ? 'experience' : 'experiences'}
                </>
              )}
              {guide.numExperiences > 0 && guide.updatedAt && ' · '}
              {guide.updatedAt && `Updated ${moment(guide.updatedAt).fromNow()}`}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

const CompanyCarousel = ({
  companyName, guides, onClick, showLocks = false,
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 296; // card width + gap
    el.scrollBy({left: direction * cardWidth, behavior: 'smooth'});
    // Delay update to let smooth scroll finish
    setTimeout(updateScrollState, 350);
  };

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg text-gray-900">{companyName}</h2>
        {guides.length >= 2 && (
          <div className={cx('flex items-center gap-1.5', guides.length < 4 && 'md:hidden')}>
            <button
              type="button"
              onClick={() => scroll(-1)}
              disabled={!canScrollLeft}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default transition-colors"
              aria-label="Scroll left"
            >
              <LeftArrowIcon className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              disabled={!canScrollRight}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default transition-colors"
              aria-label="Scroll right"
            >
              <RightArrowIcon className="w-3.5 h-3.5" stroke="currentColor" />
            </button>
          </div>
        )}
      </div>
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mb-2"
        style={{scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch'}}
      >
        {guides.map((guide) => (
          <GuideCard
            key={guide.slug}
            guide={guide}
            onClick={onClick}
            isLocked={showLocks && !guide.free}
          />
        ))}
      </div>
    </div>
  );
};

export const Placeholder = () => (
  <div className="min-w-[260px] max-w-[280px] flex-shrink-0 animate-pulse">
    <div className="flex flex-col h-full text-left p-5 rounded-xl border border-gray-200">
      <div className="w-10 h-10 rounded-lg bg-gray-100 mb-3" />
      <div className="h-5 w-3/4 bg-gray-100 rounded mb-2" />
      <div className="h-3 w-1/2 bg-gray-100 rounded mb-3" />
      <div className="h-3 w-full bg-gray-100 rounded mb-1.5" />
      <div className="h-3 w-5/6 bg-gray-100 rounded mb-1.5" />
      <div className="h-3 w-2/3 bg-gray-100 rounded mb-4" />
      <div className="border-t border-gray-100 pt-2">
        <div className="h-4 w-1/2 bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

export const PlaceholderSection = () => (
  <div className="mb-10">
    <div className="h-7 w-32 bg-gray-100 rounded mb-4" />
    <div className="flex gap-4">
      {_.times(4, (i) => <Placeholder key={i} />)}
    </div>
  </div>
);


export const GuidesCtaCard = () => (
  <a
    href="https://airtable.com/appROifRg71Nsizjd/shrH6423pjEJEdqZg"
    target="_blank"
    rel="noreferrer"
    className="block"
  >
    <div className="flex flex-col h-full text-left p-5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 transition-opacity hover:opacity-90">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>
      <h3 className="text-base font-semibold text-white leading-snug mb-1">
        Can&apos;t find what you&apos;re looking for?
      </h3>
      <div className="flex-1 mb-4">
        <p className="text-sm text-white/80 leading-relaxed">
          Let us know and we&apos;ll add it to our roadmap.
        </p>
      </div>
      <div className="mt-auto bg-white hover:bg-gray-50 w-full py-2 px-4 rounded font-semibold text-center border border-gray-300 transition text-indigo-600 text-sm">
        Request guide
      </div>
    </div>
  </a>
);

const GuidesCtaBar = () => (
  <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 rounded-xl bg-indigo-50 border border-indigo-100 px-6 py-4 mb-10">
    <div>
      <p className="font-semibold text-gray-900 text-base">
        Can&apos;t find what you are looking for?
      </p>
      <p className="text-sm text-gray-600 mt-0.5">
        Let us know and we&apos;ll add it to our roadmap.
      </p>
    </div>
    <Button
      href="https://airtable.com/appROifRg71Nsizjd/shrH6423pjEJEdqZg"
      buttonType={ButtonTypes.SKEUOMORPHIC}
      className="shrink-0 w-full md:w-auto"
    >
      Request guide
    </Button>
  </div>
);

export const CompanyCarousels = ({guidesByCompany, onClick, showLocks = false}) => (
  <div>
    {guidesByCompany.map(({companyName, guides}, index) => (
      <React.Fragment key={companyName}>
        <CompanyCarousel
          companyName={companyName}
          guides={guides}
          onClick={onClick}
          showLocks={showLocks}
        />
        {index === 0 && <GuidesCtaBar />}
      </React.Fragment>
    ))}
  </div>
);

export {GuideCard, CompanyCarousel};
